import React, { useState, useRef, useEffect } from 'react';
import logoUrl from './assets/storsafe-logo.png';
import { supabase } from './supabaseClient';
import { calculatePrice, MONTHLY_PRICES, ANNUAL_MONTHLY_PRICES, PlanId } from './lib/pricing';
import { buildPagoPluxData, handlePagoPluxAuthorize, PagoPluxAuthorizeResponse } from './lib/pagoplux';
import PpxButton from './components/payments/PpxButton';

interface Props {
  onLogin:    () => void;
  onRegister: (plan: 'starter' | 'professional' | 'enterprise') => void;
  userId?:    string;
}

const PLAN_NAMES: Record<PlanId, string> = {
  starter:      'Starter',
  professional: 'Professional',
  enterprise:   'Enterprise',
};

function isPlanId(value: string | null): value is PlanId {
  return value === 'starter' || value === 'professional' || value === 'enterprise';
}

// Lee ?plan=&cycle=&autopay= de la URL — usado para reabrir el checkout de
// PagoPlux automáticamente al volver del flujo de contratación directa
// (registro → onboarding → /pricing?...&autopay=true).
function getAutopayParams(): { plan: PlanId; cycle: 'monthly' | 'annual' } | null {
  const params = new URLSearchParams(window.location.search);
  const plan = params.get('plan');
  if (params.get('autopay') !== 'true' || !isPlanId(plan)) return null;
  return { plan, cycle: params.get('cycle') === 'monthly' ? 'monthly' : 'annual' };
}


const FAQS = [
  {
    q: '¿Qué normas soporta StorSafe?',
    a: 'StorSafe está basado en la norma INEN 2266 (Transporte, almacenamiento y manejo de materiales peligrosos) y el sistema globalmente armonizado SGA para clasificación y etiquetado. Las matrices de compatibilidad y planes de emergencia siguen estas normativas vigentes en Ecuador.',
  },
  {
    q: '¿Puedo cancelar en cualquier momento?',
    a: 'Sí. No hay contratos de permanencia. Puedes cancelar tu suscripción cuando lo desees desde la configuración de tu cuenta. El acceso continúa hasta el final del período pagado.',
  },
  {
    q: '¿Mis datos están seguros?',
    a: 'Todos los datos se almacenan en servidores seguros con cifrado en tránsito y en reposo, provistos por Supabase (ISO 27001). La arquitectura multi-empresa garantiza que solo tu organización tenga acceso a su información.',
  },
  {
    q: '¿Cuántos usuarios puedo agregar?',
    a: 'Depende del plan: Starter incluye 2 usuarios, Professional hasta 10, y Enterprise es ilimitado. Puedes invitar a tu equipo con roles diferenciados: Administrador, Gestor y Visualizador.',
  },
  {
    q: '¿Qué pasa cuando vence el período de prueba?',
    a: 'Al vencer los 5 días de prueba, tu cuenta queda bloqueada hasta activar un plan de pago. Tus datos se conservan por 30 días adicionales. También puedes solicitar una extensión de 5 días directamente desde la plataforma.',
  },
];

interface PromoResult {
  valid:               boolean;
  discount_percentage: number;
  code:                string;
  error?:              string;
}

export default function PricingPage({ onLogin, onRegister, userId }: Props) {
  const autopay = useRef(getAutopayParams()).current;

  const [annual,         setAnnual]         = useState(() => autopay?.cycle !== 'monthly');
  const [policyAccepted, setPolicyAccepted] = useState(true);
  const [openFaq,        setOpenFaq]        = useState<number | null>(null);

  const [promoInput,    setPromoInput]    = useState('');
  const [promoResult,   setPromoResult]   = useState<PromoResult | null>(null);
  const [promoApplying, setPromoApplying] = useState(false);
  const [promoError,    setPromoError]    = useState('');
  const appliedCodeRef = useRef('');

  // ── Pago directo (usuario ya logueado: trial vencido o contratación directa) ──
  const [companyId,  setCompanyId]  = useState<string | null>(null);
  const [payingPlan, setPayingPlan] = useState<PlanId | null>(null);
  const [ppxData,    setPpxData]    = useState<Record<string, any> | null>(null);
  const [ppxLoading, setPpxLoading] = useState(false);
  const [ppxError,   setPpxError]   = useState('');
  const autopayTriggered = useRef(false);

  useEffect(() => {
    if (!userId) { setCompanyId(null); return; }
    supabase
      .from('profiles')
      .select('company_id')
      .eq('id', userId)
      .single()
      .then(({ data }) => setCompanyId(data?.company_id ?? null));
  }, [userId]);

  const billingCycle = annual ? 'anual' : 'mensual';
  const discount     = promoResult?.valid ? promoResult.discount_percentage : 0;

  const breakdowns = {
    starter:      calculatePrice('starter',      billingCycle, discount),
    professional: calculatePrice('professional', billingCycle, discount),
    enterprise:   calculatePrice('enterprise',   billingCycle, discount),
  };

  // subtotal = precio sin IVA (lo que se muestra en las tarjetas, igual que antes)
  const prices = {
    starter:      breakdowns.starter.subtotal,
    professional: breakdowns.professional.subtotal,
    enterprise:   breakdowns.enterprise.subtotal,
  };

  const baseMonthly = annual ? ANNUAL_MONTHLY_PRICES : MONTHLY_PRICES;

  async function handleApplyPromo() {
    const code = promoInput.trim().toUpperCase().replace(/\s/g, '');
    if (!code) return;
    if (code === appliedCodeRef.current) return;

    if (!annual) {
      setPromoError('Los códigos promocionales solo aplican para planes anuales.');
      return;
    }

    setPromoApplying(true);
    setPromoError('');
    setPromoResult(null);

    // Vista previa: check_promo_code no consume usos (apply_promo_code queda
    // solo para service_role, para la confirmación del pago).
    const { data, error } = await supabase.rpc('check_promo_code', { p_code: code });

    if (error) {
      setPromoError('Error al verificar el código. Intenta de nuevo.');
      setPromoApplying(false);
      return;
    }

    if (data?.valid) {
      setPromoResult({ valid: true, discount_percentage: data.discount_percentage, code });
      appliedCodeRef.current = code;
    } else {
      setPromoError(data?.error ?? 'Código inválido.');
    }
    setPromoApplying(false);
  }

  function handlePromoInputChange(val: string) {
    setPromoInput(val.toUpperCase().replace(/\s/g, ''));
    if (promoResult) {
      setPromoResult(null);
      appliedCodeRef.current = '';
    }
    setPromoError('');
  }

  async function startPayment(planId: PlanId) {
    if (!userId || !companyId) return;

    setPpxLoading(true);
    setPpxError('');
    try {
      const breakdown = breakdowns[planId];
      const data = await buildPagoPluxData(
        breakdown,
        userId,
        PLAN_NAMES[planId],
        (response: PagoPluxAuthorizeResponse) => {
          handlePagoPluxAuthorize(response, companyId, breakdown).catch((err) => {
            console.error('Error registrando el pago de PagoPlux:', err);
          });
        }
      );
      setPpxData(data);
      setPayingPlan(planId);
    } catch (err) {
      console.error('Error preparando el pago de PagoPlux:', err);
      setPpxError('No se pudo iniciar el pago. Intenta de nuevo.');
    } finally {
      setPpxLoading(false);
    }
  }

  function handlePlanClick(planId: PlanId) {
    if (userId) {
      startPayment(planId);
    } else {
      sessionStorage.setItem('ss_pending_plan', planId);
      sessionStorage.setItem('ss_pending_cycle', annual ? 'annual' : 'monthly');
      onRegister(planId);
    }
  }

  // Flujo B: contratación directa sin trial — visitante sin sesión elige
  // "Contratar ahora" y va a registrarse con el plan/ciclo ya decidido.
  function handleDirectHire(planId: PlanId) {
    const cycle = annual ? 'annual' : 'monthly';
    window.location.href = `/register?plan=${planId}&cycle=${cycle}`;
  }

  // Al volver del onboarding del flujo directo (?plan=&cycle=&autopay=true),
  // reabrir automáticamente el checkout de PagoPlux para ese plan/ciclo.
  useEffect(() => {
    if (!autopay || autopayTriggered.current || !userId || !companyId) return;
    autopayTriggered.current = true;
    window.history.replaceState({}, '', '/pricing');
    startPayment(autopay.plan);
  }, [autopay, userId, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pricing-root">
      {/* Grid overlay */}
      <div className="pricing-grid-overlay" />

      {/* ── Header ── */}
      <header className="pricing-header">
        <div className="pricing-header-inner">
          <img src={logoUrl} alt="StorSafe" className="pricing-logo" />
          <div className="pricing-header-actions">
            <button className="pricing-btn-ghost" onClick={onLogin}>
              Iniciar sesión
            </button>
            <button className="pricing-btn-cta" onClick={() => onRegister('starter')}>
              Crear cuenta gratis
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="pricing-hero">
        <div className="pricing-hero-badge">
          <ShieldIcon />
          Normativa INEN 2266 · Ecuador
        </div>
        <h1 className="pricing-hero-title">
          Gestión segura de<br />bodegas químicas
        </h1>
        <p className="pricing-hero-sub">
          Cumplimiento INEN 2266 automatizado · Matrices de compatibilidad · Planes de emergencia
        </p>

        {/* ── Billing toggle ── */}
        <div className="pricing-toggle-wrap">
          <span className={`pricing-toggle-label ${!annual ? 'active' : ''}`}>Mensual</span>
          <button
            className={`pricing-toggle-switch ${annual ? 'on' : 'off'}`}
            onClick={() => {
              setAnnual((v) => {
                if (v) {
                  setPromoResult(null);
                  setPromoError('');
                  appliedCodeRef.current = '';
                }
                return !v;
              });
            }}
            aria-label="Cambiar a facturación anual"
          >
            <span className="pricing-toggle-knob" />
          </button>
          <span className={`pricing-toggle-label ${annual ? 'active' : ''}`}>
            Anual
            <span className="pricing-annual-badge">-20%</span>
          </span>
        </div>
      </section>

      {/* ── Plan cards ── */}
      <section className="pricing-cards">

        {/* STARTER */}
        <div className="pricing-card">
          <div className="pricing-card-header">
            <span className="pricing-plan-name">Starter</span>
            <div className="pricing-price-row">
              {discount > 0 && (
                <span className="pricing-price-original">${baseMonthly.starter}</span>
              )}
              <span className="pricing-price" style={discount > 0 ? { color: '#2FBF71' } : undefined}>
                ${prices.starter}
              </span>
              <span className="pricing-period">/mes</span>
              <span className="pricing-price-iva">+ IVA</span>
            </div>
            {annual && <p className="pricing-annual-note">Facturado ${prices.starter * 12}/año + IVA</p>}
          </div>
          <div className="pricing-card-limits">
            <LimitItem icon="🏭" text="1 bodega" />
            <LimitItem icon="🧪" text="50 productos" />
            <LimitItem icon="👤" text="2 usuarios" />
          </div>
          <div className="pricing-divider" />
          <ul className="pricing-features">
            <Feature text="Inventario químico IMDG completo" />
            <Feature text="Matriz de compatibilidad INEN 2266" />
            <Feature text="Hojas de seguridad SDS ilimitadas" />
            <Feature text="Planes de emergencia ERG 2024 automáticos" />
            <Feature text="Reportes PDF de cumplimiento" />
          </ul>
          <div className="pricing-card-btn-group">
            <button
              className={userId ? 'pricing-card-btn starter' : 'pricing-card-btn-outline'}
              onClick={() => handlePlanClick('starter')}
              disabled={!policyAccepted || (!!userId && ppxLoading && payingPlan === 'starter')}
              title={!policyAccepted ? 'Acepta la política de pagos para continuar' : undefined}
            >
              {userId ? (ppxLoading && payingPlan === 'starter' ? 'Procesando…' : 'Pagar ahora') : 'Probar gratis 5 días'}
            </button>
            {!userId && (
              <button
                className="pricing-card-btn starter"
                onClick={() => handleDirectHire('starter')}
                disabled={!policyAccepted}
                title={!policyAccepted ? 'Acepta la política de pagos para continuar' : undefined}
              >
                Contratar ahora
              </button>
            )}
          </div>
          <p className="pricing-card-note">{userId ? 'Pago seguro con PagoPlux' : 'Sin tarjeta de crédito'}</p>
        </div>

        {/* PROFESSIONAL — most popular */}
        <div className="pricing-card popular">
          <div className="pricing-popular-badge">
            <StarIcon /> MÁS POPULAR
          </div>
          <div className="pricing-card-header">
            <span className="pricing-plan-name">Professional</span>
            <div className="pricing-price-row">
              {discount > 0 && (
                <span className="pricing-price-original">${baseMonthly.professional}</span>
              )}
              <span className="pricing-price" style={discount > 0 ? { color: '#2FBF71' } : undefined}>
                ${prices.professional}
              </span>
              <span className="pricing-period">/mes</span>
              <span className="pricing-price-iva">+ IVA</span>
            </div>
            {annual && <p className="pricing-annual-note">Facturado ${prices.professional * 12}/año + IVA</p>}
          </div>
          <div className="pricing-card-limits">
            <LimitItem icon="🏭" text="5 bodegas" />
            <LimitItem icon="🧪" text="300 productos" />
            <LimitItem icon="👤" text="10 usuarios" />
          </div>
          <div className="pricing-divider" />
          <ul className="pricing-features">
            <Feature text="Todo lo del plan Starter" highlight />
            <Feature text="Etiquetado SGA personalizado" />
            <Feature text="Historial de cambios" />
            <Feature text="Reportes de cumplimiento INEN 2266 avanzados" />
            <Feature text="Soporte prioritario" />
          </ul>
          <div className="pricing-card-btn-group">
            <button
              className={userId ? 'pricing-card-btn popular' : 'pricing-card-btn-outline'}
              onClick={() => handlePlanClick('professional')}
              disabled={!policyAccepted || (!!userId && ppxLoading && payingPlan === 'professional')}
              title={!policyAccepted ? 'Acepta la política de pagos para continuar' : undefined}
            >
              {userId ? (ppxLoading && payingPlan === 'professional' ? 'Procesando…' : 'Pagar ahora') : 'Probar gratis 5 días'}
            </button>
            {!userId && (
              <button
                className="pricing-card-btn popular"
                onClick={() => handleDirectHire('professional')}
                disabled={!policyAccepted}
                title={!policyAccepted ? 'Acepta la política de pagos para continuar' : undefined}
              >
                Contratar ahora
              </button>
            )}
          </div>
          <p className="pricing-card-note">{userId ? 'Pago seguro con PagoPlux' : 'Sin tarjeta de crédito'}</p>
        </div>

        {/* ENTERPRISE */}
        <div className="pricing-card">
          <div className="pricing-card-header">
            <span className="pricing-plan-name">Enterprise</span>
            <div className="pricing-price-row">
              {discount > 0 && (
                <span className="pricing-price-original">${baseMonthly.enterprise}</span>
              )}
              <span className="pricing-price" style={discount > 0 ? { color: '#2FBF71' } : undefined}>
                ${prices.enterprise}
              </span>
              <span className="pricing-period">/mes</span>
              <span className="pricing-price-iva">+ IVA</span>
            </div>
            {annual && <p className="pricing-annual-note">Facturado ${prices.enterprise * 12}/año + IVA</p>}
          </div>
          <div className="pricing-card-limits">
            <LimitItem icon="🏭" text="Bodegas ilimitadas" />
            <LimitItem icon="🧪" text="Productos ilimitados" />
            <LimitItem icon="👤" text="Usuarios ilimitados" />
          </div>
          <div className="pricing-divider" />
          <ul className="pricing-features">
            <Feature text="Todo lo del plan Professional" highlight />
            <Feature text="Matriz de compatibilidad química avanzada" />
            <Feature text="Integración multi-bodega con alertas automáticas" />
            <Feature text="Soporte dedicado" />
          </ul>
          <div className="pricing-card-btn-group">
            <button
              className={userId ? 'pricing-card-btn starter' : 'pricing-card-btn-outline'}
              onClick={() => handlePlanClick('enterprise')}
              disabled={!policyAccepted || (!!userId && ppxLoading && payingPlan === 'enterprise')}
              title={!policyAccepted ? 'Acepta la política de pagos para continuar' : undefined}
            >
              {userId ? (ppxLoading && payingPlan === 'enterprise' ? 'Procesando…' : 'Pagar ahora') : 'Probar gratis 5 días'}
            </button>
            {!userId && (
              <button
                className="pricing-card-btn starter"
                onClick={() => handleDirectHire('enterprise')}
                disabled={!policyAccepted}
                title={!policyAccepted ? 'Acepta la política de pagos para continuar' : undefined}
              >
                Contratar ahora
              </button>
            )}
          </div>
          <p className="pricing-card-note">{userId ? 'Pago seguro con PagoPlux' : 'Sin tarjeta de crédito'}</p>
        </div>

      </section>

      {/* ── Servicios adicionales ── */}
      <section className="pricing-addons">
        <div className="pricing-addons-inner">
          <h2 className="pricing-addons-title">Servicios adicionales</h2>
          <p className="pricing-addons-sub">Disponibles para todos los planes · Cotización personalizada</p>
          <div className="pricing-addon-card">
            <div className="pricing-addon-icon">
              <LayoutProIcon />
            </div>
            <div className="pricing-addon-body">
              <span className="pricing-addon-name">Layout Pro</span>
              <p className="pricing-addon-desc">
                Diseño profesional de distribución de bodegas químicas según INEN 2266.
                Planos de zonas, rutas de evacuación y señalización. Cotización según tamaño y complejidad.
              </p>
            </div>
            <button
              className="pricing-addon-btn"
              onClick={async () => {
                const { data } = await supabase.auth.getSession();
                if (data.session) {
                  sessionStorage.setItem('ss_init_tab', 'layout');
                  onLogin();
                } else {
                  onRegister('starter');
                }
              }}
            >
              Ver Layout Pro
            </button>
          </div>
        </div>
      </section>

      {ppxError && (
        <p style={{ textAlign: 'center', color: '#f87171', fontSize: '0.85rem', margin: '0 0 16px' }}>
          {ppxError}
        </p>
      )}
      {ppxData && <PpxButton data={ppxData} />}

      {/* ── Promo code ── */}
      <div className="pricing-promo-box">
        <p className="pricing-promo-label">¿Tienes un código promocional?</p>
        <div className="pricing-promo-row">
          <input
            className="pricing-promo-input"
            type="text"
            placeholder="CÓDIGO"
            value={promoInput}
            onChange={(e) => handlePromoInputChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
            disabled={promoApplying}
            maxLength={32}
          />
          <button
            className="pricing-promo-btn"
            onClick={handleApplyPromo}
            disabled={promoApplying || !promoInput.trim()}
          >
            {promoApplying ? <span className="pricing-spinner" /> : 'Aplicar'}
          </button>
        </div>
        {promoResult?.valid && (
          <p className="pricing-promo-success">
            ¡Código <strong>{promoResult.code}</strong> aplicado — {promoResult.discount_percentage}% de descuento
          </p>
        )}
        {promoError && (
          <p className="pricing-promo-error">{promoError}</p>
        )}
      </div>

      {/* ── Policy agreement ── */}
      <div className="pricing-policy-box">
        <label className="pricing-policy-label">
          <input
            type="checkbox"
            className="pricing-policy-checkbox"
            checked={policyAccepted}
            onChange={(e) => setPolicyAccepted(e.target.checked)}
          />
          <span>
            Acepto la{' '}
            <a
              href="/politica-de-pagos"
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-policy-link"
            >
              política de pagos
            </a>
          </span>
        </label>
      </div>

      {/* ── Trust strip ── */}
      <section className="pricing-trust">
        <TrustItem icon={<ShieldCheckIcon />} text="Datos cifrados y seguros" />
        <TrustItem icon={<CheckCircleIcon />} text="Cumplimiento INEN 2266" />
        <TrustItem icon={<ClockIcon />}       text="5 días de prueba gratis" />
        <TrustItem icon={<XCircleIcon />}     text="Sin tarjeta de crédito" />
      </section>

      {/* ── FAQ ── */}
      <section className="pricing-faq">
        <h2 className="pricing-faq-title">Preguntas frecuentes</h2>
        <div className="pricing-faq-list">
          {FAQS.map((faq, i) => (
            <div key={i} className={`pricing-faq-item ${openFaq === i ? 'open' : ''}`}>
              <button
                className="pricing-faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{faq.q}</span>
                <ChevronIcon open={openFaq === i} />
              </button>
              {openFaq === i && (
                <div className="pricing-faq-a">{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section className="pricing-cta-banner">
        <h2 className="pricing-cta-title">Empieza a cumplir hoy</h2>
        <p className="pricing-cta-sub">5 días de prueba gratuita · Sin tarjeta de crédito · Cancela cuando quieras</p>
        <button
          className="pricing-btn-cta large"
          onClick={() => onRegister('starter')}
          disabled={!policyAccepted}
          title={!policyAccepted ? 'Acepta la política de pagos para continuar' : undefined}
        >
          {policyAccepted ? 'Crear cuenta gratis →' : 'Acepta la política para continuar'}
        </button>
      </section>

      {/* ── Footer ── */}
      <footer className="pricing-footer">
        <span>© 2026 StorSafe · Todos los derechos reservados</span>
        <div className="pricing-footer-links">
          <button className="pricing-footer-link" onClick={onLogin}>Términos y condiciones</button>
          <span className="pricing-footer-sep">·</span>
          <a className="pricing-footer-link" href="mailto:soporte@storsafe.app">Contacto</a>
        </div>
      </footer>

      <style>{PRICING_CSS}</style>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function LimitItem({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="pricing-limit-item">
      <span className="pricing-limit-icon">{icon}</span>
      <span className="pricing-limit-text">{text}</span>
    </div>
  );
}

function Feature({ text, highlight }: { text: string; highlight?: boolean }) {
  return (
    <li className={`pricing-feature ${highlight ? 'highlight' : ''}`}>
      <span className="pricing-feature-check">✓</span>
      {text}
    </li>
  );
}

function TrustItem({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="pricing-trust-item">
      {icon}
      <span>{text}</span>
    </div>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2"
      style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2L3 7v6c0 5.25 3.75 10.15 9 11.25C17.25 23.15 21 18.25 21 13V7L12 2z" />
    </svg>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2L3 7v6c0 5.25 3.75 10.15 9 11.25C17.25 23.15 21 18.25 21 13V7L12 2z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function XCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function LayoutProIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const PRICING_CSS = `
  .pricing-root {
    min-height: 100vh;
    background: linear-gradient(160deg, #0a1628 0%, #0f172a 40%, #0d1f35 100%);
    color: #e2e8f0;
    font-family: inherit;
    position: relative;
    overflow-x: hidden;
  }

  .pricing-grid-overlay {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(rgba(29, 111, 163, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(29, 111, 163, 0.04) 1px, transparent 1px);
    background-size: 48px 48px;
    pointer-events: none;
    z-index: 0;
  }

  .pricing-root > * { position: relative; z-index: 1; }

  /* ── Header ── */
  .pricing-header {
    position: sticky;
    top: 0;
    background: rgba(10, 22, 40, 0.85);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(51, 65, 85, 0.6);
    z-index: 100;
  }

  .pricing-header-inner {
    max-width: 1100px;
    margin: 0 auto;
    padding: 14px 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .pricing-logo {
    height: 36px;
    width: auto;
    filter: drop-shadow(0 0 8px rgba(47,191,113,0.2));
  }

  .pricing-header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .pricing-btn-ghost {
    background: transparent;
    border: 1px solid #334155;
    color: #94a3b8;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }

  .pricing-btn-ghost:hover {
    border-color: #64748b;
    color: #e2e8f0;
  }

  .pricing-btn-cta {
    background: linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%);
    border: none;
    color: #fff;
    padding: 9px 20px;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
    box-shadow: 0 4px 14px rgba(47,191,113,0.25);
    text-decoration: none;
    display: inline-block;
  }

  .pricing-btn-cta:hover {
    opacity: 0.92;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(47,191,113,0.35);
  }

  .pricing-btn-cta.large {
    padding: 14px 36px;
    font-size: 1rem;
    border-radius: 10px;
  }

  /* ── Hero ── */
  .pricing-hero {
    text-align: center;
    padding: 72px 24px 48px;
    max-width: 760px;
    margin: 0 auto;
  }

  .pricing-hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 5px 14px;
    background: rgba(29, 111, 163, 0.12);
    border: 1px solid rgba(29, 111, 163, 0.3);
    border-radius: 20px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #60a5fa;
    letter-spacing: 0.03em;
    margin-bottom: 24px;
  }

  .pricing-hero-title {
    font-size: clamp(2rem, 5vw, 3.2rem);
    font-weight: 800;
    line-height: 1.15;
    color: #f1f5f9;
    margin-bottom: 16px;
    background: linear-gradient(135deg, #f1f5f9 40%, #2FBF71 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .pricing-hero-sub {
    font-size: 1rem;
    color: #64748b;
    line-height: 1.7;
    margin-bottom: 36px;
  }

  /* Toggle */
  .pricing-toggle-wrap {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    background: rgba(30, 41, 59, 0.6);
    border: 1px solid #334155;
    border-radius: 30px;
  }

  .pricing-toggle-label {
    font-size: 0.88rem;
    font-weight: 500;
    color: #64748b;
    transition: color 0.2s;
  }

  .pricing-toggle-label.active { color: #e2e8f0; }

  .pricing-annual-badge {
    display: inline-block;
    margin-left: 6px;
    padding: 2px 7px;
    background: rgba(47,191,113,0.15);
    border: 1px solid rgba(47,191,113,0.3);
    color: #2FBF71;
    font-size: 0.68rem;
    font-weight: 700;
    border-radius: 10px;
    vertical-align: middle;
  }

  .pricing-toggle-switch {
    position: relative;
    width: 44px;
    height: 24px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
    padding: 0;
    flex-shrink: 0;
  }

  .pricing-toggle-switch.off { background: #334155; }
  .pricing-toggle-switch.on  { background: #2FBF71; }

  .pricing-toggle-knob {
    position: absolute;
    top: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  }

  .pricing-toggle-switch.off .pricing-toggle-knob { left: 3px; }
  .pricing-toggle-switch.on  .pricing-toggle-knob { left: 23px; }

  /* ── Cards ── */
  .pricing-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
    max-width: 1060px;
    margin: 0 auto 60px;
    padding: 0 24px;
  }

  .pricing-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 16px;
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 0;
    position: relative;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .pricing-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(0,0,0,0.3);
  }

  .pricing-card.popular {
    border-color: #2FBF71;
    background: linear-gradient(160deg, #1a2e22 0%, #1e293b 100%);
    box-shadow: 0 0 0 1px rgba(47,191,113,0.2), 0 8px 32px rgba(47,191,113,0.12);
  }

  .pricing-popular-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 12px;
    background: rgba(47,191,113,0.15);
    border: 1px solid rgba(47,191,113,0.35);
    color: #2FBF71;
    font-size: 0.65rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    border-radius: 20px;
    margin-bottom: 16px;
    width: fit-content;
  }

  .pricing-card-header { margin-bottom: 20px; }

  .pricing-plan-name {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 8px;
  }

  .pricing-price-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .pricing-price {
    font-size: 2.8rem;
    font-weight: 800;
    color: #f1f5f9;
    line-height: 1;
  }

  .pricing-period {
    font-size: 0.9rem;
    color: #64748b;
    font-weight: 500;
  }

  .pricing-price-iva {
    font-size: 0.72rem;
    color: #475569;
    font-weight: 500;
    margin-left: 2px;
  }

  .pricing-annual-note {
    font-size: 0.75rem;
    color: #64748b;
    margin-top: 4px;
  }

  .pricing-card-limits {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }

  .pricing-limit-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.05);
    border-radius: 8px;
  }

  .pricing-limit-icon { font-size: 1rem; flex-shrink: 0; }

  .pricing-limit-text {
    font-size: 0.85rem;
    font-weight: 600;
    color: #cbd5e1;
  }

  .pricing-divider {
    height: 1px;
    background: #263045;
    margin: 16px 0;
  }

  .pricing-features {
    list-style: none;
    padding: 0;
    margin: 0 0 24px;
    display: flex;
    flex-direction: column;
    gap: 9px;
    flex: 1;
  }

  .pricing-feature {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    font-size: 0.84rem;
    color: #94a3b8;
    line-height: 1.4;
  }

  .pricing-feature.highlight { color: #cbd5e1; font-weight: 600; }

  .pricing-feature-check {
    color: #2FBF71;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .pricing-card-btn {
    display: block;
    width: 100%;
    padding: 13px;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    text-align: center;
    text-decoration: none;
    transition: opacity 0.2s, transform 0.15s;
    border: none;
  }

  .pricing-card-btn.starter,
  .pricing-card-btn.popular {
    background: linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%);
    color: #fff;
    box-shadow: 0 4px 16px rgba(47,191,113,0.25);
  }

  .pricing-card-btn.enterprise {
    background: transparent;
    border: 1px solid #334155;
    color: #94a3b8;
  }

  .pricing-card-btn:hover { opacity: 0.9; transform: translateY(-1px); }

  .pricing-card-btn-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .pricing-card-btn-outline {
    display: block;
    width: 100%;
    padding: 13px;
    border-radius: 10px;
    font-size: 0.95rem;
    font-weight: 700;
    cursor: pointer;
    text-align: center;
    text-decoration: none;
    transition: opacity 0.2s, transform 0.15s, border-color 0.2s, color 0.2s;
    background: transparent;
    border: 1px solid #334155;
    color: #94a3b8;
  }

  .pricing-card-btn-outline:hover:not(:disabled) {
    border-color: #64748b;
    color: #e2e8f0;
    transform: translateY(-1px);
  }

  .pricing-card-note {
    text-align: center;
    font-size: 0.72rem;
    color: #475569;
    margin-top: 8px;
  }

  .pricing-price-original {
    font-size: 1.4rem;
    font-weight: 600;
    color: #475569;
    text-decoration: line-through;
    margin-right: 4px;
    line-height: 1;
    align-self: center;
  }

  /* ── Servicios adicionales ── */
  .pricing-addons {
    max-width: 1060px;
    margin: 0 auto 48px;
    padding: 0 24px;
  }

  .pricing-addons-inner {
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid #334155;
    border-radius: 16px;
    padding: 32px 36px;
  }

  .pricing-addons-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #e2e8f0;
    margin: 0 0 4px;
  }

  .pricing-addons-sub {
    font-size: 0.8rem;
    color: #64748b;
    margin: 0 0 24px;
  }

  .pricing-addon-card {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 20px 24px;
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 12px;
    flex-wrap: wrap;
  }

  .pricing-addon-icon {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: rgba(29,111,163,0.12);
    border: 1px solid rgba(29,111,163,0.25);
    color: #60a5fa;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pricing-addon-body {
    flex: 1;
    min-width: 200px;
  }

  .pricing-addon-name {
    display: block;
    font-size: 1rem;
    font-weight: 700;
    color: #e2e8f0;
    margin-bottom: 6px;
  }

  .pricing-addon-desc {
    font-size: 0.84rem;
    color: #94a3b8;
    line-height: 1.6;
    margin: 0;
  }

  .pricing-addon-btn {
    flex-shrink: 0;
    padding: 10px 22px;
    background: linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%);
    border: none;
    border-radius: 8px;
    color: #fff;
    font-size: 0.88rem;
    font-weight: 700;
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
    transition: opacity 0.2s, transform 0.15s;
    box-shadow: 0 4px 12px rgba(47,191,113,0.2);
  }

  .pricing-addon-btn:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  /* ── Promo code ── */
  .pricing-promo-box {
    max-width: 480px;
    margin: 0 auto 16px;
    padding: 20px 28px;
    background: rgba(30, 41, 59, 0.5);
    border: 1px solid #334155;
    border-radius: 12px;
    text-align: center;
  }

  .pricing-promo-label {
    font-size: 0.82rem;
    font-weight: 600;
    color: #64748b;
    margin: 0 0 12px;
  }

  .pricing-promo-row {
    display: flex;
    gap: 8px;
    justify-content: center;
  }

  .pricing-promo-input {
    flex: 1;
    max-width: 240px;
    padding: 9px 14px;
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 8px;
    color: #e2e8f0;
    font-size: 0.88rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    outline: none;
    transition: border-color 0.2s;
  }

  .pricing-promo-input:focus { border-color: #2FBF71; }
  .pricing-promo-input::placeholder { color: #334155; font-weight: 500; letter-spacing: 0.02em; }

  .pricing-promo-btn {
    padding: 9px 20px;
    background: linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%);
    border: none;
    border-radius: 8px;
    color: #fff;
    font-size: 0.88rem;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.2s;
    min-width: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pricing-promo-btn:hover:not(:disabled) { opacity: 0.88; }
  .pricing-promo-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  .pricing-promo-success {
    margin: 10px 0 0;
    font-size: 0.82rem;
    color: #2FBF71;
  }

  .pricing-promo-error {
    margin: 10px 0 0;
    font-size: 0.82rem;
    color: #f87171;
  }

  .pricing-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: pricing-spin 0.7s linear infinite;
  }

  @keyframes pricing-spin { to { transform: rotate(360deg); } }

  /* ── Policy agreement ── */
  .pricing-policy-box {
    max-width: 760px;
    margin: -28px auto 40px;
    padding: 14px 28px;
    display: flex;
    justify-content: center;
  }

  .pricing-policy-label {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 0.84rem;
    font-weight: 600;
    color: #cbd5e1;
    line-height: 1.4;
  }

  .pricing-policy-checkbox {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    accent-color: #2FBF71;
    cursor: pointer;
  }

  .pricing-policy-link {
    color: #2FBF71;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .pricing-policy-link:hover {
    color: #4ade80;
  }

  .pricing-card-btn:disabled,
  .pricing-card-btn-outline:disabled,
  .pricing-btn-cta:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  /* ── Trust strip ── */
  .pricing-trust {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 32px;
    padding: 28px 24px;
    border-top: 1px solid #1e293b;
    border-bottom: 1px solid #1e293b;
    background: rgba(15, 23, 42, 0.4);
    margin-bottom: 72px;
  }

  .pricing-trust-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.84rem;
    color: #64748b;
  }

  .pricing-trust-item svg { color: #2FBF71; }

  /* ── FAQ ── */
  .pricing-faq {
    max-width: 720px;
    margin: 0 auto 80px;
    padding: 0 24px;
  }

  .pricing-faq-title {
    font-size: 1.6rem;
    font-weight: 700;
    color: #f1f5f9;
    text-align: center;
    margin-bottom: 32px;
  }

  .pricing-faq-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    border: 1px solid #334155;
    border-radius: 12px;
    overflow: hidden;
  }

  .pricing-faq-item {
    border-bottom: 1px solid #263045;
  }

  .pricing-faq-item:last-child { border-bottom: none; }

  .pricing-faq-q {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 20px;
    background: #1e293b;
    border: none;
    cursor: pointer;
    text-align: left;
    font-size: 0.9rem;
    font-weight: 600;
    color: #cbd5e1;
    transition: background 0.15s, color 0.15s;
  }

  .pricing-faq-q:hover { background: #253347; color: #f1f5f9; }
  .pricing-faq-item.open .pricing-faq-q { color: #2FBF71; background: rgba(47,191,113,0.04); }

  .pricing-faq-a {
    padding: 4px 20px 18px;
    font-size: 0.86rem;
    color: #94a3b8;
    line-height: 1.7;
    background: #1e293b;
  }

  /* ── CTA Banner ── */
  .pricing-cta-banner {
    text-align: center;
    padding: 72px 24px;
    background: linear-gradient(135deg, rgba(11, 60, 93, 0.4) 0%, rgba(29, 111, 163, 0.2) 50%, rgba(47, 191, 113, 0.1) 100%);
    border-top: 1px solid rgba(29, 111, 163, 0.2);
    border-bottom: 1px solid rgba(29, 111, 163, 0.2);
  }

  .pricing-cta-title {
    font-size: 2rem;
    font-weight: 800;
    color: #f1f5f9;
    margin-bottom: 10px;
  }

  .pricing-cta-sub {
    font-size: 0.9rem;
    color: #64748b;
    margin-bottom: 28px;
  }

  /* ── Footer ── */
  .pricing-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    padding: 24px 40px;
    font-size: 0.78rem;
    color: #334155;
  }

  .pricing-footer-links {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .pricing-footer-link {
    background: none;
    border: none;
    color: #334155;
    cursor: pointer;
    font-size: 0.78rem;
    text-decoration: none;
    padding: 0;
    transition: color 0.2s;
  }

  .pricing-footer-link:hover { color: #64748b; }
  .pricing-footer-sep { color: #1e293b; }

  @media (max-width: 640px) {
    .pricing-header-inner { padding: 12px 16px; }
    .pricing-hero { padding: 48px 16px 32px; }
    .pricing-cards { padding: 0 16px; grid-template-columns: 1fr; }
    .pricing-footer { padding: 20px 16px; justify-content: center; text-align: center; }
  }
`;
